import { Check, Copy, CopyPlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBlockCopy } from './block-copy-context';
import { useBlockDeleteBlocked } from './block-delete-guard-context';

import { IconButton } from '@/components/ui/icon-button';

export function useArmedDelete(onRemove: () => void, onDuplicate: () => void) {
  const blockRef = useRef<HTMLDivElement>(null);
  const [isDeleteArmed, setIsDeleteArmed] = useState(false);

  useEffect(() => {
    if (!isDeleteArmed) return;
    function handleOutside(e: MouseEvent | TouchEvent) {
      if (blockRef.current && !blockRef.current.contains(e.target as Node)) {
        setIsDeleteArmed(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isDeleteArmed]);

  function triggerDuplicate() {
    onDuplicate();
  }

  function triggerDelete() {
    if (isDeleteArmed) {
      onRemove();
    } else {
      setIsDeleteArmed(true);
    }
  }

  function disarmDelete() {
    setIsDeleteArmed(false);
  }

  return { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete };
}

interface BlockActionsProps {
  /** Which block these act on. Without it there is no Copy — the embedded editor panel has no
   * canvas card to copy from, and passes none. */
  blockId?: string;
  isDeleteArmed: boolean;
  onTriggerDuplicate: () => void;
  onTriggerDelete: () => void;
  onDisarm: () => void;
}

export default function BlockActions({ blockId, isDeleteArmed, onTriggerDuplicate, onTriggerDelete, onDisarm }: BlockActionsProps) {
  const { t } = useTranslation();
  const clipboard = useBlockCopy();
  const justCopied = !!blockId && clipboard?.justCopiedId === blockId;
  /** Disabled with the reason on hover, the way the variables panel and the spine builder
   *  already say this — see `block-delete-guard-context`. */
  const deleteBlocked = useBlockDeleteBlocked(blockId);
  return (
    <div className='ml-auto flex shrink-0 items-center gap-0.5'>
      <div aria-live='assertive' aria-atomic='true' className='sr-only'>
        {isDeleteArmed ? t('blocks.actions.deleteArmed') : ''}
      </div>
      {/* The horizontal twin of the action rail's Copy. It has to be here too: which of the two
          layouts a block draws depends on the viewport, so a Copy in only one of them means the
          clipboard is unreachable at some window sizes. */}
      {clipboard && blockId && (
        <IconButton
          variant='primary'
          onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); clipboard.copy(blockId); }}
          onClick={(e) => { e.stopPropagation(); if (e.detail === 0) clipboard.copy(blockId); }}
          aria-label={justCopied ? t('task.paste.copied') : t('task.paste.copyBlock')}
        >
          {justCopied ? <Check className='h-4 w-4 text-ws-success-fg' /> : <Copy className='h-4 w-4' />}
        </IconButton>
      )}
      <IconButton
        variant='primary'
        onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onTriggerDuplicate(); }}
        onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onTriggerDuplicate(); }}
        aria-label={t('blocks.actions.duplicate')}
      >
        <CopyPlus className='h-4 w-4' />
      </IconButton>
      <IconButton
        variant='destructive'
        isArmed={isDeleteArmed}
        disabled={!!deleteBlocked}
        onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onTriggerDelete(); }}
        onPointerLeave={(e) => { if (isDeleteArmed && e.pointerType !== 'touch') onDisarm(); }}
        onBlur={() => { if (isDeleteArmed) onDisarm(); }}
        onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onTriggerDelete(); }}
        title={deleteBlocked}
        aria-label={deleteBlocked ?? (isDeleteArmed ? t('blocks.actions.confirmDelete') : t('blocks.actions.delete'))}
      >
        <Trash2 className='h-4 w-4' />
      </IconButton>
    </div>
  );
}
